import React from 'react';
import { Button, Card, Col, InputNumber, Row, Space, Spin, Typography } from 'antd';
import PropTypes from 'prop-types';

import { ReloadOutlined } from '@ant-design/icons';

import './styles.scss';

const { Text } = Typography;

const StatisticsPanel = ({ params, onParamChange, onReset, loading, hasDefaults }) => {
    const formatCurrency = (value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const parseCurrency = (value) => value.replace(/\$\s?|(,*)/g, '');

    return (
        <Card className="statistics-panel" title="Simulation Parameters">
            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Spin size="large" />
                </div>
            ) : (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Starting Equity</Text>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    value={params.startingEquity}
                                    formatter={formatCurrency}
                                    parser={parseCurrency}
                                    min={0}
                                    step={100}
                                    onChange={(value) => onParamChange('startingEquity', value)}
                                />
                            </Space>
                        </Col>

                        <Col span={24}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Win Percentage (%)</Text>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    value={params.winPercentage}
                                    min={0}
                                    max={100}
                                    step={0.1}
                                    precision={2}
                                    onChange={(value) => onParamChange('winPercentage', value)}
                                />
                            </Space>
                        </Col>

                        <Col span={24}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Average Winner/Loser Ratio</Text>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    value={params.avgWinnerLoserRatio}
                                    min={0}
                                    step={0.1}
                                    precision={2}
                                    onChange={(value) =>
                                        onParamChange('avgWinnerLoserRatio', value)
                                    }
                                />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Loss Rate = 1.0
                                </Text>
                            </Space>
                        </Col>

                        <Col span={24}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Average Trades per Month</Text>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    value={params.avgTradesPerMonth}
                                    min={0}
                                    step={1}
                                    precision={0}
                                    onChange={(value) => onParamChange('avgTradesPerMonth', value)}
                                />
                            </Space>
                        </Col>

                        <Col span={24}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Risk per Trade</Text>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    value={params.riskPerTrade}
                                    formatter={formatCurrency}
                                    parser={parseCurrency}
                                    min={0}
                                    step={10}
                                    precision={2}
                                    onChange={(value) => onParamChange('riskPerTrade', value)}
                                />
                            </Space>
                        </Col>
                    </Row>

                    {hasDefaults && (
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={onReset}
                            block
                            style={{ marginTop: 16 }}
                        >
                            Reset to Defaults
                        </Button>
                    )}

                    {!hasDefaults && (
                        <div
                            style={{
                                marginTop: 16,
                                padding: 16,
                                background: '#f5f5f5',
                                borderRadius: 8
                            }}
                        >
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                No trade data available. Using default values. Start trading to see
                                personalized defaults.
                            </Text>
                        </div>
                    )}
                </Space>
            )}
        </Card>
    );
};

StatisticsPanel.propTypes = {
    params: PropTypes.shape({
        startingEquity: PropTypes.number.isRequired,
        winPercentage: PropTypes.number.isRequired,
        avgWinnerLoserRatio: PropTypes.number.isRequired,
        avgTradesPerMonth: PropTypes.number.isRequired,
        riskPerTrade: PropTypes.number.isRequired
    }).isRequired,
    onParamChange: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    hasDefaults: PropTypes.bool
};

StatisticsPanel.defaultProps = {
    loading: false,
    hasDefaults: true
};

export default StatisticsPanel;
